/**
 * use-apps — apps 域 API hooks（ADM-APP-S001~S011）
 */

import type {
  AppPaymentMethodResponse,
  AppPlatform,
  AppResponse,
  CopyAppRequest,
  CreateAppRequest,
  ReadingPassLanguageConfigResponse,
  UpdateAppRequest,
  UpsertReadingPassLanguageConfigRequest,
} from '@novel-admin/shared';
import { apiRequest, apiRequestOffsetPaged } from '~/api/client';

type RolloutAppResponse = Omit<AppResponse, 'platform'> & {
  platform?: AppPlatform;
};

function normalizeAppPlatformForRollout(app: RolloutAppResponse): AppResponse {
  if (app.platform === 'IOS' || app.platform === 'ANDROID' || app.platform === 'WEB') {
    return app as AppResponse;
  }
  if (app.platform !== undefined) {
    throw new Error(`App ${app.id} 返回了无效平台；请刷新运营后台后重试`);
  }

  const hasIos = Boolean(app.bundle_id_ios?.trim());
  const hasAndroid = Boolean(app.package_name_android?.trim());
  if (hasIos === hasAndroid) {
    throw new Error(`App ${app.id} 无法安全确定平台；请刷新运营后台后重试`);
  }
  return {
    ...app,
    platform: hasIos ? 'IOS' : 'ANDROID',
  };
}

export async function listApps(platform?: AppPlatform): Promise<AppResponse[]> {
  const apps: AppResponse[] = [];
  const appsPageLimit = 200;
  for (let pageNumber = 1; ; pageNumber += 1) {
    const page = await apiRequestOffsetPaged<RolloutAppResponse>({
      method: 'GET',
      path: '/api/v1/apps',
      query: {
        page: pageNumber,
        limit: appsPageLimit,
        platform,
      },
    });
    const normalizedRows = page.data.map(normalizeAppPlatformForRollout);
    apps.push(
      ...(platform === undefined
        ? normalizedRows
        : normalizedRows.filter((app) => app.platform === platform)),
    );
    if (!page.hasMore || page.data.length === 0) break;
  }
  return apps;
}

export async function getApp(id: string): Promise<AppResponse> {
  return apiRequest<AppResponse>({ method: 'GET', path: `/api/v1/apps/${id}` });
}

export async function createApp(body: CreateAppRequest): Promise<AppResponse> {
  return apiRequest<AppResponse>({ method: 'POST', path: '/api/v1/apps', body });
}

export async function updateApp(id: string, body: UpdateAppRequest): Promise<AppResponse> {
  return apiRequest<AppResponse>({ method: 'PATCH', path: `/api/v1/apps/${id}`, body });
}

export async function copyApp(sourceAppId: string, body: CopyAppRequest): Promise<AppResponse> {
  return apiRequest<AppResponse>({
    method: 'POST',
    path: `/api/v1/apps/${sourceAppId}/copy`,
    body,
  });
}

export async function enableApp(id: string): Promise<AppResponse> {
  return apiRequest<AppResponse>({ method: 'POST', path: `/api/v1/apps/${id}/enable` });
}

export async function disableApp(id: string): Promise<AppResponse> {
  return apiRequest<AppResponse>({ method: 'POST', path: `/api/v1/apps/${id}/disable` });
}

export async function listAppPaymentMethods(appId: string): Promise<AppPaymentMethodResponse[]> {
  return apiRequest<AppPaymentMethodResponse[]>({
    method: 'GET',
    path: `/api/v1/apps/${appId}/payment-methods`,
  });
}

/** 小时阅读卡语言独立配置：五项整份替换，删除后回退 App 默认。 */
export const readingPassLanguageConfigsApi = {
  list: (appId: string) =>
    apiRequest<ReadingPassLanguageConfigResponse[]>({
      method: 'GET',
      path: `/api/v1/apps/${appId}/reading-pass-language-configs`,
    }),
  upsert: (appId: string, language: string, body: UpsertReadingPassLanguageConfigRequest) =>
    apiRequest<ReadingPassLanguageConfigResponse>({
      method: 'PUT',
      path: `/api/v1/apps/${appId}/reading-pass-language-configs/${encodeURIComponent(language)}`,
      body,
    }),
  remove: (appId: string, language: string) =>
    apiRequest<null>({
      method: 'DELETE',
      path: `/api/v1/apps/${appId}/reading-pass-language-configs/${encodeURIComponent(language)}`,
    }),
};
